import { writable } from 'svelte/store';
import { v4 as uuidv4 } from 'uuid';

const LOCAL_STORAGE_KEY = 'itemStore';

function createItemStore() {
  const storedItems = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
  const { subscribe, update } = writable(storedItems);

  subscribe(items => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
  });

  const createNewItem = (text) => {
    const id = uuidv4();
    const variables = extractVariables(text);

    const item = {
      id,
      text,
      variables: variables.map(v => {
        return { name: v, values: [], active: false };
      }),
      finalCommand: text // Initialize with the template text
    };

    update(items => [...items, item]);
  };

  const addVariableValue = (itemId, variableName, variableValue) => {
    update(items => {
      return items.map(item => {
        if (item.id === itemId) {
          item.variables = item.variables.map(variable => {
            if (variable.name === variableName) {
              variable.values.forEach(v => v.active = false); // Deactivate all values
              const newValue = { value: variableValue, active: true };
              variable.values.push(newValue);
            }
            return variable;
          });
          item.finalCommand = calculateFinalCommand(item); // Recalculate final command
        }
        return item;
      });
    });
  };

  const updateVariableActiveState = (itemId, variableName) => {
    update(items => {
      return items.map(item => {
        if (item.id === itemId) {
          item.variables.forEach(variable => {
            variable.active = variable.name === variableName;
          });
          item.finalCommand = calculateFinalCommand(item); // Recalculate final command
        }
        return item;
      });
    });
  };

  const updateVariableValueActiveState = (itemId, variableName, value) => {
    update(items => {
      return items.map(item => {
        if (item.id === itemId) {
          item.variables.forEach(variable => {
            if (variable.name === variableName) {
              variable.values.forEach(v => v.active = v.value === value);
            }
          });
          item.finalCommand = calculateFinalCommand(item); // Recalculate final command
        }
        return item;
      });
    });
  };

  const deleteVariableValue = (itemId, variableName, value) => {
    update(items => {
      return items.map(item => {
        if (item.id === itemId) {
          item.variables = item.variables.map(variable => {
            if (variable.name === variableName) {
              variable.values = variable.values.filter(v => v.value !== value);
              if (variable.values.length > 0) {
                variable.values[0].active = true; // Set the first value as active if there are values left
              }
            }
            return variable;
          });
          item.finalCommand = calculateFinalCommand(item); // Recalculate final command
        }
        return item;
      });
    });
  };

  const deleteItem = (itemId) => {
    update(items => items.filter(item => item.id !== itemId));
  };

  const calculateFinalCommand = (item) => {
    let command = item.text;
    item.variables.forEach(variable => {
      const activeValue = variable.values.find(v => v.active);
      const value = activeValue ? activeValue.value : `$${variable.name}`;
      command = command.replace(new RegExp(`\\$${variable.name}`, 'g'), value);
    });
    return command;
  };

  return {
    subscribe,
    addItem: createNewItem,
    addVariableValue,
    updateVariableActiveState,
    updateVariableValueActiveState,
    deleteVariableValue,
    deleteItem
  };
}

function extractVariables(command) {
  const regex = /\$([^\s]+)/g;
  let variables = [];
  let match;
  while ((match = regex.exec(command)) !== null) {
    variables.push(match[1]);
  }
  return variables;
}


export const itemStore = createItemStore();
